import { Type } from '@angular/core';

/**
 * Storyblok `animation` option values. Keep in sync with Storyblok schema options
 * and add a matching entry to ANIMATION_REGISTRY when shipping a new animation.
 */
export type AnimationKey = 'version_comparison' | 'image_analysis' | 'integrations_orbit' | 'integrations_publishing';

export type AnimationInputs = {
  width: number;
  height: number;
};

export type AnimationDefinition = {
  /** Angular component that renders the animation */
  component: Type<unknown>;
  /** Intrinsic size used when Storyblok width/height are empty */
  defaultWidth: number;
  defaultHeight: number;
};

const loadedAnimationDefinitions: { [key: string]: AnimationDefinition } = {};

const loadAnimationDefinition = async (key: AnimationKey): Promise<AnimationDefinition> => {
  if (key === 'version_comparison') {
    const { VersionComparisonAnimationComponent } =
      await import('./version-comparison/version-comparison-animation.component');
    return {
      component: VersionComparisonAnimationComponent,
      defaultWidth: 680,
      defaultHeight: 680,
    };
  }
  if (key === 'image_analysis') {
    const { ImageAnalysisAnimationComponent } = await import('./image-analysis/image-analysis-animation.component');
    return {
      component: ImageAnalysisAnimationComponent,
      defaultWidth: 680,
      defaultHeight: 680,
    };
  }
  if (key === 'integrations_orbit') {
    const { IntegrationsOrbitAnimationComponent } =
      await import('./integrations-orbit/integrations-orbit-animation.component');
    return {
      component: IntegrationsOrbitAnimationComponent,
      defaultWidth: 680,
      defaultHeight: 680,
    };
  }
  if (key === 'integrations_publishing') {
    const { IntegrationsPublishingAnimationComponent } =
      await import('./integrations-publishing/integrations-publishing-animation.component');
    return {
      component: IntegrationsPublishingAnimationComponent,
      defaultWidth: 680,
      defaultHeight: 560,
    };
  }
};

export const getAnimationDefinition = async (key: AnimationKey): Promise<AnimationDefinition> => {
  if (loadedAnimationDefinitions[key]) return loadedAnimationDefinitions[key];
  const definition = await loadAnimationDefinition(key);
  loadedAnimationDefinitions[key] = definition;
  return definition;
};

export const resolveAnimationSize = (
  def: AnimationDefinition,
  width?: number | string | null,
  height?: number | string | null
): { width: number; height: number } => {
  const parsedWidth = Number(width);
  const parsedHeight = Number(height);

  return {
    width: Number.isFinite(parsedWidth) && parsedWidth > 0 ? parsedWidth : (def?.defaultWidth ?? 1200),
    height: Number.isFinite(parsedHeight) && parsedHeight > 0 ? parsedHeight : (def?.defaultHeight ?? 900),
  };
};
